/**
 * normalizeMapScale
 *
 * 맵 렌더링 마지막 단계의 크기 비율 보정.
 *
 * 배경:
 *   현장 도면(DXF)마다 사용 단위가 다르고, elementFactory 의 초기 baseScale 도
 *   "도면 전체 폭 / TARGET_WIDTH" 로 동적 결정되어 왔다. 그 결과 동일한 한국 표준
 *   주차구역(2.5m × 5m)이 osong, central, banpo 등 현장별로 서로 다른 픽셀 크기로
 *   렌더링된다. (osong: 11.61 × 23.23, banpo: 198.32 × 396.64 등)
 *
 * 목적:
 *   모든 현장에서 일반 주차구역(p-parking-basic) 단변이 항상 198.32 픽셀이 되도록
 *   graph 전체에 동일한 비율(ratio = STANDARD / measuredShort) 을 곱한다.
 *   주차구역 외 모든 객체(CCTV, 비상벨, 기둥, 화살표 등) 도 같은 비율로 보정되므로
 *   도면 내부의 상대 위치/크기 관계는 그대로 보존된다.
 *
 * Idempotency:
 *   |ratio - 1| < 0.02 이면 이미 표준에 가까운 것으로 보고 skip.
 *   따라서 동일 graph 에 여러 번 호출돼도 두 번째부터는 효과 없음.
 */

import { dia } from '@joint/core'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'
import { coordsToSVGPath } from '../../../map-editor-shared/lib/csvParser'

/**
 * 반포(banpo) 현장 import 결과의 일반 주차구역 단변 길이(px). 이 값을 표준으로 삼는다.
 * elementFactory.STANDARD_PARKING_SHORT_PX 와 동일해야 한다.
 */
export const STANDARD_PARKING_SHORT_PX = 198.32

/** 표준값과 측정값의 비율이 이 임계값 이내면 보정하지 않는다. */
const RATIO_TOLERANCE = 0.02

export interface NormalizeResult {
  applied: boolean
  ratio: number
  measuredShort: number | null
  parkingSampleCount: number
  totalElements: number
  reason: string
}

/**
 * graph 안의 p-parking-basic element 의 단변 중앙값을 측정한다.
 * 없으면 다른 p-parking-* 레이어 중 샘플이 가장 많은 것을 사용.
 */
function measureMedianParkingShort(graph: dia.Graph): { median: number | null; count: number } {
  const byLayer = new Map<string, number[]>()
  for (const cell of graph.getCells()) {
    if (!cell.isElement()) continue
    const id = String(cell.id || '')
    if (!id.startsWith('p-parking')) continue
    const layerPrefix = id.split('_')[0]
    const el = cell as dia.Element
    const size = el.size()
    if (size.width <= 0 || size.height <= 0) continue
    const short = Math.min(size.width, size.height)
    if (!byLayer.has(layerPrefix)) byLayer.set(layerPrefix, [])
    byLayer.get(layerPrefix)!.push(short)
  }

  if (byLayer.size === 0) return { median: null, count: 0 }

  const preferred = byLayer.get('p-parking-basic')
  const chosen = preferred && preferred.length > 0
    ? preferred
    : [...byLayer.values()].sort((a, b) => b.length - a.length)[0]

  const sorted = [...chosen].sort((a, b) => a - b)
  return {
    median: sorted[Math.floor(sorted.length / 2)],
    count: chosen.length,
  }
}

/**
 * 표준 스케일 px 거리(자동 관계 반경 등)를 이 graph 좌표 거리로 바꾸는 배율.
 * 처음 import 한 도면은 원본 스케일(예: 단변 ~10px)이고 저장 후 다시 열면 표준
 * 스케일(198.32px)로 보정되므로, px 값을 그대로 쓰면 같은 반경이 열 때마다 다른
 * 실제 거리가 된다. 주차구역이 없어 잴 수 없으면 1.
 */
export function graphDistanceScale(graph: dia.Graph): number {
  const { median } = measureMedianParkingShort(graph)
  return median ? median / STANDARD_PARKING_SHORT_PX : 1
}

/**
 * 단일 element 의 좌표를 ratio 만큼 스케일한다.
 *   - position, size 모두 곱
 *   - data.vertices 가 있으면 (standard.Path 주차구역/구역/기둥/화살표 등) 좌표 재계산 후
 *     attrs.body.d 를 다시 빌드. 이게 없으면 size 만 변하고 내부 path 가 옛 좌표라 깨진다.
 *   - data.originalPosition 도 일치 시킨다.
 */
function scaleElement(el: dia.Element, ratio: number): void {
  const pos = el.position()
  const size = el.size()
  el.position(pos.x * ratio, pos.y * ratio)
  el.resize(Math.max(size.width * ratio, 1), Math.max(size.height * ratio, 1))

  const data = el.get('data') as
    | {
        vertices?: [number, number][]
        originalPosition?: { x: number; y: number }
        type?: string
      }
    | undefined

  if (data?.vertices && Array.isArray(data.vertices) && data.vertices.length > 0) {
    const scaledVertices = data.vertices.map(
      ([x, y]) => [x * ratio, y * ratio] as [number, number]
    )

    const xs = scaledVertices.map(v => v[0])
    const ys = scaledVertices.map(v => v[1])
    const minX = Math.min(...xs)
    const minY = Math.min(...ys)
    const relative = scaledVertices.map(v => ({ x: v[0] - minX, y: v[1] - minY }))

    // standard.Path 만 attrs.body.d 가짐. polygon 등 다른 타입은 attrs.body.points 사용.
    const cellType = el.get('type') as string | undefined
    if (cellType === 'standard.Path') {
      el.attr('body/d', coordsToSVGPath(relative))
    } else if (cellType === 'standard.Polygon') {
      el.attr('body/refPoints', relative.map(p => `${p.x},${p.y}`).join(' '))
    }

    el.set('data', {
      ...data,
      vertices: scaledVertices,
      originalPosition: data.originalPosition
        ? { x: data.originalPosition.x * ratio, y: data.originalPosition.y * ratio }
        : undefined,
    })
  }
}

/**
 * graph 전체에 표준 비율 보정을 적용한다.
 * 한 번 적용된 graph 에 다시 호출돼도 ratio≈1 이면 skip.
 *
 * @param graph - JointJS graph
 * @param paper - (선택) JointJS paper. 주어지면 보정 직후 paper.scale 도 1/ratio 만큼
 *                축소해 화면상 보이는 크기는 보정 전과 동일하게 유지한다. 데이터만
 *                표준화되고 view-level zoom 한계는 그대로 사용 가능.
 */
export function normalizeMapScale(
  graph: dia.Graph | null,
  paper?: dia.Paper | null
): NormalizeResult {
  if (!graph) {
    return {
      applied: false,
      ratio: 1,
      measuredShort: null,
      parkingSampleCount: 0,
      totalElements: 0,
      reason: 'no-graph',
    }
  }

  const totalElements = graph.getElements().length
  const { median, count } = measureMedianParkingShort(graph)

  if (median === null || count === 0) {
    return {
      applied: false,
      ratio: 1,
      measuredShort: null,
      parkingSampleCount: 0,
      totalElements,
      reason: 'no-parking-elements',
    }
  }

  const ratio = STANDARD_PARKING_SHORT_PX / median

  if (Math.abs(ratio - 1) < RATIO_TOLERANCE) {
    log.info(LogCategory.GENERAL, 'normalizeMapScale: already at standard scale', {
      medianShort: median.toFixed(2),
      ratio: ratio.toFixed(4),
      samples: count,
    })
    return {
      applied: false,
      ratio,
      measuredShort: median,
      parkingSampleCount: count,
      totalElements,
      reason: 'already-standard',
    }
  }

  log.info(LogCategory.GENERAL, 'normalizeMapScale: applying scale correction', {
    medianShort: median.toFixed(2),
    standardShort: STANDARD_PARKING_SHORT_PX,
    ratio: ratio.toFixed(4),
    samples: count,
    totalElements,
  })

  graph.startBatch('normalize-scale')
  try {
    for (const cell of graph.getCells()) {
      if (!cell.isElement()) continue
      scaleElement(cell as dia.Element, ratio)
    }
  } finally {
    graph.stopBatch('normalize-scale')
  }

  // paper view 도 1/ratio 만큼 축소 — 데이터는 ratio 배 커졌지만 화면에 보이는 크기는
  // 보정 전과 동일하게 유지된다. zoom 한계 충돌 없이 모든 객체가 한 viewport 에 보이게 함.
  // translate 도 동일 비율로 축소해야 동일 좌표가 화면의 동일 위치에 머무른다.
  if (paper) {
    const curScale = paper.scale()
    const curTranslate = paper.translate()
    paper.scale(curScale.sx / ratio, curScale.sy / ratio)
    paper.translate(curTranslate.tx / ratio, curTranslate.ty / ratio)

    // 보정 직후 자동 fit — graph 전체가 viewport 한 화면에 들어오도록.
    // 우하단 미니맵(약 200×150 + margin) 영역을 피하기 위해 padding 비대칭으로 줌.
    // scaleContentToFit 는 padding 객체 형태도 지원: { top, right, bottom, left }
    try {
      paper.scaleContentToFit({
        padding: { top: 40, right: 240, bottom: 180, left: 40 },
        maxScale: 4,
        minScale: 0.01,
      })
    } catch (e) {
      // ignore — some paper instances may not support object-padding
    }
  }

  return {
    applied: true,
    ratio,
    measuredShort: median,
    parkingSampleCount: count,
    totalElements,
    reason: 'normalized',
  }
}
